import type { Story } from '@/@types/story'
import { type MouseEvent, useEffect, useRef } from 'react'
import StoryMediaElement from './StoryMediaElement'
import { getStoryPreviewUrl, type StoryMedia } from './storyMedia'
import styles from './StoryMedia.module.css'

const SINGLE_CLICK_DELAY_MS = 500

export interface StoryMediaFrameProps {
	story: Story
	media?: StoryMedia
	onTogglePlayback: () => void
	onToggleMuted: () => void
	onPlaybackBlocked: () => void
	onPlaybackTimeChange: (currentTime: number) => void
	onRetry?: () => Promise<void>
	onLoadingChange: (loading: boolean) => void
	onDoubleClickLike?: () => void
}

export default function StoryMediaFrame({
	story,
	media,
	onTogglePlayback,
	onToggleMuted,
	onPlaybackBlocked,
	onPlaybackTimeChange,
	onRetry,
	onLoadingChange,
	onDoubleClickLike,
}: StoryMediaFrameProps) {
	const playbackClickTimerRef = useRef<ReturnType<typeof setTimeout>>()
	const previewUrl = getStoryPreviewUrl(story)
	const handleMediaClick = () => {
		if (media?.type !== 'VIDEO') return

		clearTimeout(playbackClickTimerRef.current)
		playbackClickTimerRef.current = window.setTimeout(onTogglePlayback, SINGLE_CLICK_DELAY_MS)
	}

	const handleMediaDoubleClick = (event: MouseEvent<HTMLDivElement>) => {
		if (event.target instanceof Element && event.target.closest('button')) return

		clearTimeout(playbackClickTimerRef.current)
		onDoubleClickLike?.()
	}

	useEffect(() => () => clearTimeout(playbackClickTimerRef.current), [])

	return (
		<div
			className={styles.mediaFrame}
			onClick={handleMediaClick}
			onDoubleClick={handleMediaDoubleClick}
		>
			{media ? (
				<StoryMediaElement
					media={media}
					onTogglePlayback={onTogglePlayback}
					onToggleMuted={onToggleMuted}
					onPlaybackBlocked={onPlaybackBlocked}
					onPlaybackTimeChange={onPlaybackTimeChange}
					onRetry={onRetry}
					onLoadingChange={onLoadingChange}
				/>
			) : (
				previewUrl && <img src={previewUrl} alt="" referrerPolicy="no-referrer" draggable={false} />
			)}
			<span className={styles.storyMask} aria-hidden="true" />
		</div>
	)
}
